import type { BuildingModel, PlacedWall, Vec2 } from '@bw/building';
import { planToWall, wallToPlan } from '@bw/building';
import type { WallPlacement } from '@bw/document';

/**
 * The arithmetic behind dragging a wall around in plan (phase 2, M5).
 *
 * Kept out of the controller and away from the DOM so it can be tested as
 * plain functions: a drag is a placement in, a pointer offset, a placement
 * out. The controller is left holding only gesture bookkeeping.
 */

/** Rotation snaps to this while dragging, unless the bypass modifier is held. */
export const ANGLE_STEP_DEG = 15;

export interface PlanSnap {
  readonly placement: WallPlacement;
  /** Points the move latched onto, for the snap marks the painter draws. */
  readonly marks: readonly Vec2[];
}

/** The two ends of a wall's centreline, in world plan coordinates. */
export function centrelineEnds(wall: PlacedWall): readonly Vec2[] {
  return wall.centreline === null ? [] : [wall.centreline[0], wall.centreline[1]];
}

/** Every centreline end in the building except the given wall's. */
export function otherEnds(building: BuildingModel, exceptId: string): Vec2[] {
  const out: Vec2[] = [];
  for (const wall of building.walls) {
    if (wall.id === exceptId) continue;
    out.push(...centrelineEnds(wall));
  }
  return out;
}

/** The centreline ends a placement would have, without rebuilding the model. */
export function endsForPlacement(wall: PlacedWall, placement: WallPlacement): Vec2[] {
  if (wall.extent === null) return [];
  const half = placement.thicknessMm / 2;
  return [
    wallToPlan(placement, wall.extent.minX, half),
    wallToPlan(placement, wall.extent.maxX, half),
  ];
}

/**
 * Move a wall, then pull it onto a neighbour if one of its ends lands close
 * enough to one of theirs.
 *
 * Snapping translates the whole wall by the residual rather than stretching
 * it: a wall's length is decided by the bricks in it, and the plan view has no
 * business changing that.
 */
export function moveWall(
  wall: PlacedWall,
  dx: number,
  dz: number,
  targets: readonly Vec2[],
  toleranceMm: number,
): PlanSnap {
  const moved: WallPlacement = {
    ...wall.placement,
    origin: {
      x: wall.placement.origin.x + dx,
      y: wall.placement.origin.y,
      z: wall.placement.origin.z + dz,
    },
  };
  if (toleranceMm <= 0) return { placement: moved, marks: [] };

  let best: { dx: number; dz: number; at: Vec2; distance: number } | null = null;
  for (const end of endsForPlacement(wall, moved)) {
    for (const target of targets) {
      const distance = Math.hypot(target.x - end.x, target.z - end.z);
      if (distance > toleranceMm) continue;
      if (best === null || distance < best.distance) {
        best = { dx: target.x - end.x, dz: target.z - end.z, at: target, distance };
      }
    }
  }
  if (best === null) return { placement: moved, marks: [] };

  const snapped: WallPlacement = {
    ...moved,
    origin: {
      x: moved.origin.x + best.dx,
      y: moved.origin.y,
      z: moved.origin.z + best.dz,
    },
  };
  return { placement: snapped, marks: [best.at] };
}

/**
 * The angle, in the plan, of a direction from one point to another.
 *
 * A wall at rotation r runs along (cos r, -sin r), so its plan angle is -r.
 * Everything here works in placement degrees and converts at the edges, which
 * keeps the sign confusion in one place instead of spread through the drag.
 */
export function planAngleDeg(from: Vec2, to: Vec2): number {
  return (Math.atan2(to.z - from.z, to.x - from.x) * 180) / Math.PI;
}

/** Normalise to [0, 360). */
export function normaliseDeg(value: number): number {
  const wrapped = value % 360;
  return wrapped < 0 ? wrapped + 360 : wrapped;
}

/**
 * Rotate a wall about the start of its centreline.
 *
 * The pivot is the end you have already placed, which is what makes a rotation
 * feel like swinging a wall into position rather than throwing it across the
 * drawing. The origin is recomputed to hold that pivot still.
 */
export function rotateWall(
  wall: PlacedWall,
  rotationDeg: number,
  snapToStep: boolean,
): WallPlacement {
  const stepped = snapToStep
    ? Math.round(rotationDeg / ANGLE_STEP_DEG) * ANGLE_STEP_DEG
    : rotationDeg;
  const rotation = normaliseDeg(stepped);
  if (wall.extent === null) return { ...wall.placement, rotationDeg: rotation };

  const half = wall.placement.thicknessMm / 2;
  const pivot = wallToPlan(wall.placement, wall.extent.minX, half);
  // Where that same local point would land with no translation at all.
  const offset = wallToPlan(
    { ...wall.placement, rotationDeg: rotation, origin: { x: 0, y: 0, z: 0 } },
    wall.extent.minX,
    half,
  );
  return {
    ...wall.placement,
    rotationDeg: rotation,
    origin: { x: pivot.x - offset.x, y: wall.placement.origin.y, z: pivot.z - offset.z },
  };
}

/** Is this plan point inside the wall's rectangle? */
export function hitWall(wall: PlacedWall, at: Vec2): boolean {
  if (wall.extent === null) return false;
  const local = planToWall(wall.placement, at.x, at.z);
  return (
    local.x >= wall.extent.minX &&
    local.x <= wall.extent.maxX &&
    local.z >= 0 &&
    local.z <= wall.placement.thicknessMm
  );
}

/**
 * The topmost wall under a plan point.
 *
 * Later walls win, matching the paint order, so clicking an overlapping corner
 * grabs the one drawn on top — which is the one you can see.
 */
export function wallAt(building: BuildingModel, at: Vec2): PlacedWall | null {
  for (let i = building.walls.length - 1; i >= 0; i--) {
    const wall = building.walls[i] as PlacedWall;
    if (hitWall(wall, at)) return wall;
  }
  return null;
}
